/**
 * API client for the Quality Radar application
 * Implements the HTTP client approach from ADR-019
 */
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { getEnvironment } from '../config/environment';

/**
 * API client for making HTTP requests to backend services
 */
export class ApiClient {
  private client: AxiosInstance;

  /**
   * Create a new API client instance
   */
  constructor() {
    const env = getEnvironment();
    const baseURL = env.apiEndpoint || '/api';

    const config: AxiosRequestConfig = {
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest', // CSRF protection
      },
      // Only send credentials in browser environment
      withCredentials: env.isBrowser,
    };

    this.client = axios.create(config);

    // Configure request interceptor for authentication
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Only attempt to get auth token in browser environment
        if (env.isBrowser) {
          const token = this.getAuthToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      }
    );

    // Configure response interceptor for error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: unknown) => {
        if (error.response?.status === 401 && this.isTokenExpiredError(error)) {
          try {
            await this.refreshToken();
            // Retry the original request with the new token
            const newToken = this.getAuthToken();
            if (newToken && error.config) {
              error.config.headers.Authorization = `Bearer ${newToken}`;
              return this.client(error.config);
            }
          } catch (refreshError) {
            // If refresh fails, handle unauthorized state
            if (env.isBrowser) {
              this.handleUnauthorized();
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Make a GET request to the specified endpoint
   * @param url API endpoint
   * @param params URL parameters
   * @returns Promise resolving to the response data
   */
  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await this.client.get<T>(url, { params });
    return response.data;
  }

  /**
   * Make a POST request to the specified endpoint
   * @param url API endpoint
   * @param data Request body
   * @returns Promise resolving to the response data
   */
  async post<T>(url: string, data: unknown): Promise<T> {
    const response = await this.client.post<T>(url, data);
    return response.data;
  }

  /**
   * Make a PUT request to the specified endpoint
   * @param url API endpoint
   * @param data Request body
   * @returns Promise resolving to the response data
   */
  async put<T>(url: string, data: unknown): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  /**
   * Make a DELETE request to the specified endpoint
   * @param url API endpoint
   * @returns Promise resolving to the response data
   */
  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }

  /**
   * Get the current authentication token
   */
  private getAuthToken(): string | null {
    // In a real implementation, this would use a secure token storage strategy
    return localStorage.getItem('auth_token');
  }

  /**
   * Determine if an error is due to an expired token
   * @param error Axios error object
   */
  private isTokenExpiredError(error: unknown): boolean {
    const axiosError = error as { response?: { data?: { code?: string } } };
    return axiosError.response?.data?.code === 'TOKEN_EXPIRED';
  }

  /**
   * Refresh the authentication token
   */
  private async refreshToken(): Promise<void> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // We use a separate axios instance to avoid interceptor loops
    const response = await axios.post('/auth/refresh', { token: refreshToken });

    if (response.data?.token) {
      localStorage.setItem('auth_token', response.data.token);
    } else {
      throw new Error('Token refresh failed');
    }
  }

  /**
   * Handle unauthorized state (e.g., redirect to login)
   */
  private handleUnauthorized(): void {
    // Clear auth data
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');

    // In a real application, you might redirect to login
    console.warn('Authentication required. Please log in.');

    // Optionally redirect
    // window.location.href = '/login';
  }
}

// Create a singleton instance for application-wide use
export const apiClient = new ApiClient();
